import React, { useState } from 'react';
import ObliqueIcon from '@/components/icons/ObliqueIcon';
import { Badge } from './Badge';
import { Button } from './Button';

/**
 * Standard Oblique System Design: CredentialCard
 * Official digital security badge for trainee & employee credentials provisioning.
 */
export function CredentialCard({
  traineeName = 'Trainee Associate',
  email = '',
  temporaryPassword = '',
  seatNumber = '001',
  companyName = 'The Scale Agency',
  assignedTrack = 'The Scale Agency Blueprint',
  portalUrl = window?.location?.origin ? `${window.location.origin}/login` : 'https://app.oblique.ai/login',
  onLaunch,
  className = ''
}) {
  const [copiedField, setCopiedField] = useState(null);

  const copyToClipboard = (text, fieldName) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedField(fieldName);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const getFullInviteText = () => {
    return `Welcome to ${companyName} Training Academy!\n\nHere are your access credentials for the LMS platform:\n• Login Portal: ${portalUrl}\n• Email: ${email}\n• Temporary Password: ${temporaryPassword}\n• Assigned Curriculum: ${assignedTrack}\n• Seat ID: #${seatNumber}\n\nPlease log in and complete your modules and Certification Exam to finalize your onboarding.`;
  };

  return (
    <div className={`relative overflow-hidden rounded-[16px] bg-gradient-to-br from-slate-900 via-slate-950 to-zinc-900 border border-slate-700/60 p-5 shadow-xl text-white ${className}`}>
      {/* Background Holographic Shimmer Accent */}
      <div className="absolute top-0 right-0 w-36 h-36 bg-sky-500/10 rounded-full blur-2xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-32 h-32 bg-indigo-500/10 rounded-full blur-2xl pointer-events-none" />

      {/* Header Badge */}
      <div className="flex items-center justify-between pb-3.5 mb-3.5 border-b border-white/10 relative z-10">
        <div>
          <span className="text-[10px] font-bold uppercase tracking-widest text-sky-400 font-mono block">
            {companyName} • ACADEMY PASS
          </span>
          <h3 className="text-base font-bold text-white tracking-tight mt-0.5">
            {traineeName}
          </h3>
        </div>
        <Badge variant="neutral" size="sm" className="bg-white/10 text-white border-white/20 font-mono text-[10px]">
          SEAT #{seatNumber}
        </Badge>
      </div>

      {/* Credentials Fields */}
      <div className="space-y-2.5 text-xs relative z-10">
        {/* Email */}
        <div className="flex items-center justify-between p-2 rounded-lg bg-white/[0.04] border border-white/5">
          <span className="text-slate-400 text-[11px] font-medium">Login Email</span>
          <div className="flex items-center gap-2">
            <span className="font-mono text-slate-100 font-semibold text-xs selection:bg-sky-500">
              {email}
            </span>
            <button
              type="button"
              onClick={() => copyToClipboard(email, 'email')}
              title="Copy Email"
              className="p-1 rounded text-slate-400 hover:text-sky-300 hover:bg-white/5 transition-colors cursor-pointer border-0 bg-transparent"
            >
              <ObliqueIcon name={copiedField === 'email' ? 'check' : 'content_copy'} size={14} className={copiedField === 'email' ? 'text-emerald-400' : ''} />
            </button>
          </div>
        </div>

        {/* Temporary Password */}
        <div className="flex items-center justify-between p-2 rounded-lg bg-white/[0.04] border border-white/5">
          <span className="text-slate-400 text-[11px] font-medium">Temp Password</span>
          <div className="flex items-center gap-2">
            <span className="font-mono text-slate-100 font-semibold text-xs tracking-wider selection:bg-sky-500">
              {temporaryPassword}
            </span>
            <button
              type="button"
              onClick={() => copyToClipboard(temporaryPassword, 'pass')}
              title="Copy Password"
              className="p-1 rounded text-slate-400 hover:text-sky-300 hover:bg-white/5 transition-colors cursor-pointer border-0 bg-transparent"
            >
              <ObliqueIcon name={copiedField === 'pass' ? 'check' : 'content_copy'} size={14} className={copiedField === 'pass' ? 'text-emerald-400' : ''} />
            </button>
          </div>
        </div>

        {/* Assigned Curriculum */}
        <div className="flex items-center justify-between p-2 rounded-lg bg-white/[0.04] border border-white/5">
          <span className="text-slate-400 text-[11px] font-medium">Assigned Track</span>
          <span className="font-medium text-sky-300 text-xs truncate max-w-[200px]">
            {assignedTrack}
          </span>
        </div>
      </div>

      {/* Action Footer */}
      <div className="mt-4 pt-3 border-t border-white/10 flex flex-col sm:flex-row items-center gap-2 relative z-10">
        <Button
          variant="outline"
          size="sm"
          onClick={() => copyToClipboard(getFullInviteText(), 'invite')}
          className="w-full sm:flex-1 text-white border-white/20 hover:bg-white/10 bg-transparent text-xs"
          iconLeft={<ObliqueIcon name={copiedField === 'invite' ? 'check' : 'chat'} size={14} className={copiedField === 'invite' ? 'text-emerald-400' : ''} />}
        >
          {copiedField === 'invite' ? 'Copied Invitation!' : 'Copy Slack / Email Invite'}
        </Button>

        {onLaunch && (
          <Button
            variant="primary"
            size="sm"
            onClick={onLaunch}
            className="w-full sm:w-auto text-xs"
            iconRight={<ObliqueIcon name="arrow_forward" size={14} />}
          >
            Launch Trainee View
          </Button>
        )}
      </div>
    </div>
  );
}
